import { Typography, List } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { professions } from '../resources';
import { UPDATE_USER } from '../graphql';
import SearchInput from '../../common/inputs/searchInput';
import GeneralListItem from '../../common/lists/generalListItem';
import OvForm from '../../common/wrappers/ovForm';
import { sendAnalyticsEvent } from '../../../utils/firebase';

export interface UserState {
  companyType?: string,
  employmentStatus?: string,
  incompleteFields?: string[],
}

interface Props {
  user: UserState,
  updateUserState: (att: UserState) => void,
  onContinue?: () => void,
  cached?: boolean,
  title?: string,
  subTitle?: string,
  analyticsEvent?: string,
}

const EmployeeInfo = ({
  user, updateUserState, cached, title, subTitle, analyticsEvent, ...props
}: Props): JSX.Element => {
  const { t } = useTranslation(['base', 'user']);
  const [loading, setLoading] = useState(false);
  const [searchString, setSearchString] = useState('');
  const titleText = user && user.employmentStatus && (user.employmentStatus === 'EMPLOYED' || user.employmentStatus === 'SELF_EMPLOYED') ? t('user:employement.employementInfo.titleForEmployee') : t('user:employement.employementInfo.titleForRetiered');
  const industryOptions = professions.map(({ profession }) => profession);
  const [state, setState] = useState({
    value: '',
    sourceData: industryOptions,
    filterData: industryOptions,
  });
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const filterList = (e: any): void => {
    if (e.target && e.target.value) {
      const updatedList = state.sourceData.filter((item) => (item.toLowerCase().search(e.target.value.toLowerCase()) !== -1));
      setState({ ...state, filterData: updatedList });
    }
  };
  const [clickedValue, setClickedValue] = useState('');
  const updateCompanyTypeOnCompleted = (data: { updateUser: { user: { incompleteFields: string[] } } }): void => {
    console.log({ event: 'COMPANY_TYPE_UPDATED', companyType: user.companyType });
    updateUserState({ ...user, incompleteFields: data.updateUser.user.incompleteFields });
    setLoading(false);
    if (props.onContinue) props.onContinue();
  };
  const [updateCompanyType] = useMutation(UPDATE_USER, {
    variables: {
      input: { companyType: user.companyType },
    },
    onCompleted: updateCompanyTypeOnCompleted,
    onError: () => setLoading(false),
  });
  const onContinueClick = (): void => {
    if (analyticsEvent) sendAnalyticsEvent(analyticsEvent).then();
    if (cached) {
      if (props.onContinue) props.onContinue();
    } else {
      setLoading(true);
      updateCompanyType().then();
    }
  };
  return (
    <OvForm loading={loading} onSubmit={onContinueClick} disableButton={!user.companyType} submitButton={false}>
      <Typography variant="heading2">{title ?? titleText}</Typography>
      <Typography variant="paragraph3">{subTitle ?? t('user:employement.employementInfo.subTitle')}</Typography>
      <SearchInput
        label={t('user:employement.employementInfo.placeholder')}
        value={searchString}
        onChange={(event) => {
          setSearchString(event.target.value);
          filterList(event);
        }}
        onClear={() => {
          setState({ ...state, filterData: industryOptions });
          setClickedValue('');
          setSearchString('');
          updateUserState({ ...user, companyType: '' });
        }}
      />
      <List className="on-boarding-list">
        {state.filterData.map((value: string) => (
          <GeneralListItem
            text={value}
            key={value}
            hasRadioField
            checked={clickedValue === value || value === user.companyType}
            onClick={() => {
              if (value) {
                setSearchString(value);
                setClickedValue(value);
                updateUserState({ ...user, companyType: value });
              }
            }}
          />
        ))}
      </List>
    </OvForm>
  );
};

EmployeeInfo.defaultProps = {
  onContinue: undefined,
  cached: false,
  title: undefined,
  subTitle: undefined,
  analyticsEvent: undefined,
};

export default EmployeeInfo;
